import { Button, Form, Input, Select, Table, message } from "antd";
import axios from "axios";
import React, { useCallback, useEffect, useState } from "react";
import { serverUrl } from "../../../../../nestserver";
import BackButton from "../../../../../components/buttons/BackButton";

const GRADE_OPTIONS = [
  { label: "Excellent (EX)", value: "EX", color: "#4CAF50" },
  { label: "A+", value: "A+", color: "#66BB6A" },
  { label: "A", value: "A", color: "#81C784" },
  { label: "B+", value: "B+", color: "#FFB300" },
  { label: "B", value: "B", color: "#FB8C00" },
];

const MontessoriViewEditCard = () => {
  const [form] = Form.useForm();
  const [studentId, setStudentId] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [reportId, setReportId] = useState("");
  const [domains, setDomains] = useState([]);
  const [academicYear, setAcademicYear] = useState("");
  const [activeTerms, setActiveTerms] = useState([]);
  const [selectedTerm, setSelectedTerm] = useState("");
  const [isLocked, setIsLocked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [allMetadata, setAllMetadata] = useState([]);

  useEffect(() => {
    const urlParts = window.location.pathname.split("/");
    const templateIdIndex = urlParts.indexOf("template");
    const studentIdIndex = urlParts.indexOf("montessori-student");
    const reportIdIndex = urlParts.indexOf("report");

    if (templateIdIndex !== -1 && studentIdIndex !== -1) {
      setTemplateId(urlParts[templateIdIndex + 1]);
      setStudentId(urlParts[studentIdIndex + 1]);
    }
    if (reportIdIndex !== -1) {
      setReportId(urlParts[reportIdIndex + 1]);
    }
  }, []);

  const fetchTemplate = useCallback(async () => {
    if (!templateId) return;
    try {
      const res = await axios({
        method: "get",
        url: `${serverUrl}/montessori-progress-card-template/${templateId}`,
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
        },
      });
      const data = res.data?.result?.data;
      setActiveTerms(data?.active_terms || []);
      if (data?.active_terms?.length > 0 && !selectedTerm) {
        setSelectedTerm(data.active_terms[0]);
      }
    } catch (error) {
      console.error("Error fetching template:", error);
    }
  }, [templateId, selectedTerm]);

  const fetchScores = useCallback(async () => {
    if (!studentId || !templateId || !selectedTerm) return;
    setLoading(true);
    try {
      const res = await axios({
        method: "get",
        url: `${serverUrl}/montessori-progress-card/score/student/${studentId}/template/${templateId}?term=${selectedTerm}`,
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
        },
      });
      const data = res.data?.result;

      const mappedDomains = data?.domains?.map((domain) => ({
        ...domain,
        milestones: domain.milestones.map((m) => {
          const assessment = m.assessments?.find(
            (a) => a.term === selectedTerm,
          );
          return {
            ...m,
            grade: assessment?.grade || "",
            comment: assessment?.comment || "",
          };
        }),
      }));

      setDomains(mappedDomains || []);
      setIsLocked(data?.is_locked || false);

      const metadata = data?.term_metadata?.find(
        (m) => m.term === selectedTerm,
      );
      const student = data?.student;

      form.setFieldsValue({
        name:
          data?.name ||
          (student ? `${student.first_name} ${student.last_name}` : ""),
        class: data?.class?.name || student?.class?.name || data?.class || "",
        studentId: data?.student_id || student?.unique_id || "",
        parent:
          data?.parent ||
          (student?.parent
            ? `${student.parent.first_name} ${student.parent.last_name}`
            : ""),
        phone: data?.phone || student?.parent?.phone || "",
        overall_comment: metadata?.overall_comment || "",
        status: metadata?.status || "draft",
      });
      setAllMetadata(data?.term_metadata || []);
      setAcademicYear(data?.academic_year || "");
    } catch (error) {
      console.error("Error fetching scores:", error);
    } finally {
      setLoading(false);
    }
  }, [studentId, templateId, selectedTerm, form]);

  useEffect(() => {
    fetchTemplate();
  }, [templateId, fetchTemplate]);

  useEffect(() => {
    fetchScores();
  }, [studentId, templateId, selectedTerm, fetchScores]);

  const handleMilestoneGradeChange = (domainIndex, milestoneIndex, grade) => {
    setDomains((prev) => {
      const updated = [...prev];
      const domain = { ...updated[domainIndex] };
      const milestones = [...domain.milestones];
      const milestone = { ...milestones[milestoneIndex] };
      milestone.grade = grade;
      const assessments = milestone.assessments
        ? [...milestone.assessments]
        : [];
      const idx = assessments.findIndex((a) => a.term === selectedTerm);
      if (idx > -1) {
        assessments[idx] = { ...assessments[idx], grade };
      } else {
        assessments.push({ term: selectedTerm, grade, comment: "" });
      }
      milestone.assessments = assessments;
      milestones[milestoneIndex] = milestone;
      domain.milestones = milestones;
      updated[domainIndex] = domain;
      return updated;
    });
  };

  const onFinish = async (values) => {
    if (isLocked) {
      message.error("This term is locked.");
      return;
    }
    const payload = {
      template: templateId,
      student: studentId,
      term: selectedTerm,
      overall_comment: values?.overall_comment,
      status: values?.status || "draft",
      academic_year: academicYear,
      domains: domains.map((domain) => ({
        name: domain.name,
        milestones: domain.milestones.map((m) => ({
          description: m.description,
          grade: m.grade || "",
          comment: m.comment,
        })),
      })),
    };
    try {
      await axios.put(
        `${serverUrl}/montessori-progress-card/${reportId}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
          },
        },
      );
      message.success("Progress Card updated successfully");
      fetchScores();
    } catch (err) {
      message.error("Error updating progress card");
    }
  };

  const getMilestoneColumns = (domainIndex) => {
    const termColumns = activeTerms.map((term) => ({
      title: term,
      key: `term-${term}`,
      align: "center",
      width: 120,
      render: (_, record, milestoneIndex) => {
        const isCurrentTerm = term === selectedTerm;
        const assessment = record.assessments?.find((a) => a.term === term);
        const grade = isCurrentTerm ? record.grade : assessment?.grade;

        if (isCurrentTerm) {
          return (
            <Form.Item noStyle>
              <Select
                disabled={isLocked}
                value={grade || undefined}
                onChange={(value) =>
                  handleMilestoneGradeChange(domainIndex, milestoneIndex, value)
                }
                placeholder="-"
                style={{ width: "100%" }}
              >
                {GRADE_OPTIONS.map((opt) => (
                  <Select.Option key={opt.value} value={opt.value}>
                    <span style={{ color: opt.color, fontWeight: "bold" }}>
                      {opt.value}
                    </span>
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          );
        }

        return (
          <div
            className="font-bold text-base"
            style={{
              color:
                GRADE_OPTIONS.find((o) => o.value === grade)?.color || "#333",
            }}
          >
            {grade || "--"}
          </div>
        );
      },
    }));

    return [
      {
        title: "Milestone",
        dataIndex: "description",
        key: "description",
        render: (text) => <div className="text-sm text-gray-700">{text}</div>,
      },
      ...termColumns,
    ];
  };

  return (
    <div className="p-8">
      <div className="bg-white p-10 rounded-2xl">
        <div className="flex justify-between items-center pb-4">
          <div className="flex items-center gap-4">
            <span className="font-semibold text-gray-600">Viewing Term:</span>
            <Select
              value={selectedTerm}
              onChange={(v) => setSelectedTerm(v)}
              style={{ width: 150 }}
              placeholder="Select Term"
            >
              {activeTerms.map((term) => (
                <Select.Option key={term} value={term}>
                  {term}
                </Select.Option>
              ))}
            </Select>
            {isLocked && (
              <span className="text-red-500 font-bold ml-4 bg-red-50 px-3 py-1 rounded">
                LOCKED
              </span>
            )}
            {loading && (
              <span className="text-orange-500 text-sm">Loading...</span>
            )}
          </div>
          <div className="flex gap-4">
            <BackButton />
          </div>
        </div>
        <div className="text-2xl text-orange-500 font-semibold pb-1">
          Montessori Progress Report Card - {selectedTerm}
        </div>
        <Form
          layout="vertical"
          name="montessori-view-edit"
          form={form}
          onFinish={onFinish}
          autoComplete="off"
        >
          <div className="form-grid-2">
            <Form.Item label="Name" name="name">
              <Input readOnly />
            </Form.Item>
            <Form.Item label="Class" name="class">
              <Input readOnly />
            </Form.Item>
            <Form.Item label="Student Id" name="studentId">
              <Input readOnly />
            </Form.Item>
            <Form.Item label="Parent" name="parent">
              <Input readOnly />
            </Form.Item>
            <Form.Item label="Phone" name="phone">
              <Input readOnly />
            </Form.Item>
          </div>

          {/* Domains & Milestones */}
          <div className="mt-4 mb-6">
            <div className="text-lg font-semibold text-gray-700 mb-4 flex justify-between">
              <span>Categories & Milestones ({selectedTerm})</span>
            </div>
            {domains.map((domain, domainIndex) => (
              <div
                key={domainIndex}
                className="mb-6 border border-gray-200 rounded-lg"
              >
                <div
                  className="px-5 py-4 border-b flex justify-between items-center rounded-t-lg shadow-sm"
                  style={{ backgroundColor: "#f97316", borderColor: "#ea580c" }}
                >
                  <span
                    className="font-extrabold uppercase tracking-widest text-base"
                    style={{ color: "#ffffff" }}
                  >
                    {domain.name}
                  </span>
                  <span
                    className="text-xs font-bold px-2 py-1 rounded"
                    style={{
                      backgroundColor: "rgba(0,0,0,0.1)",
                      color: "#ffffff",
                    }}
                  >
                    Category
                  </span>
                </div>
                <Table
                  columns={getMilestoneColumns(domainIndex)}
                  dataSource={domain.milestones || []}
                  pagination={false}
                  rowKey={(record, i) => `${domainIndex}-${i}`}
                  size="small"
                />
              </div>
            ))}
          </div>

          {/* General Remarks Grid */}
          <div className="mt-8 mb-8">
            <div className="text-lg font-semibold text-gray-700 mb-4">
              General Remarks
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {activeTerms.map((term) => {
                const isCurrent = term === selectedTerm;
                return (
                  <div
                    key={term}
                    className={`p-4 rounded-lg border flex flex-col ${
                      isCurrent
                        ? "border-orange-200 bg-orange-50"
                        : "border-gray-100 bg-gray-50"
                    }`}
                  >
                    <div className="font-bold text-orange-700 mb-2">{term}</div>
                    {isCurrent ? (
                      <Form.Item name="overall_comment" noStyle>
                        <Input.TextArea
                          disabled={isLocked}
                          rows={4}
                          placeholder={`Enter remarks for ${term}...`}
                          className="w-full border-none focus:ring-0 bg-transparent resize-none p-0 text-sm"
                        />
                      </Form.Item>
                    ) : (
                      <div className="text-sm text-gray-600 min-h-[80px] whitespace-pre-wrap">
                        {allMetadata?.find((m) => m.term === term)
                          ?.overall_comment || "No remarks entered."}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="h-10"></div>

          {/* Grading Norms */}
          <div className="bg-gray-50 p-6 rounded-xl mb-8 border border-gray-100 mt-12">
            <div className="text-sm font-bold text-gray-500 mb-3 uppercase tracking-wider">
              Grading Norms
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {GRADE_OPTIONS.map((opt) => (
                <div key={opt.value} className="flex flex-col">
                  <span
                    style={{ color: opt.color }}
                    className="font-bold text-base"
                  >
                    {opt.value}
                  </span>
                  <span className="text-xs text-gray-500">
                    {opt.label.split("(")[0]}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-6">
            <Form.Item
              label="Term Status"
              name="status"
              style={{ width: "200px" }}
            >
              <Select
                disabled={isLocked}
              >
                <Select.Option value="draft">Draft</Select.Option>
                <Select.Option value="ready">Ready</Select.Option>
              </Select>
            </Form.Item>
            {!isLocked && (
              <Form.Item className="mb-0 mt-6">
                <Button
                  size="large"
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                >
                  Update Progress Card
                </Button>
              </Form.Item>
            )}
          </div>
        </Form>
      </div>
    </div>
  );
};

export default MontessoriViewEditCard;
